import {createSlice, type PayloadAction} from "@reduxjs/toolkit";

import {buildApiQueryError, type QueryError} from "../util/errorUtil";
import type {ApiQueryError} from "./types";

export const LAYER_WOHNLAGEN_MIKRO_DECORATED_BACKGROUND = "reva:wohnlagen_mikro_decorated_background" as const;
export const LAYER_WOHNLAGEN_MIKRO_KREIS = "reva:wohnlage_karte_mikro_kreis" as const;
export const LAYER_WOHNLAGEN_MIKRO_DECORATED_TOP = "reva:wohnlagen_mikro_decorated_top" as const;

export const LAYER_WOHNLAGEN_MAKRO = "reva:wohnlage_karte_makro_3857" as const;

export const LAYER_POIS_DECORATED = "reva:pois_decorated" as const;
export const LAYER_ALKIS = "reva:alkis" as const;
export const LAYER_BASEMAP = "reva:de_basemapde_web_raster_farbe" as const;
export const LAYER_COPERNICUS = "reva:VHR_2021_LAEA" as const;
export const DEFAULT_MATRIXSET = "WebMercatorQuadx2" as const;

/**
 * A GeoServer layer known to REVA. The set is fixed (the layers are hard-coded above), so it is
 * modelled as a string-literal union — usable both as a value (the `LAYER_*` constants) and as a type.
 */
export type WmtsLayer =
    | typeof LAYER_WOHNLAGEN_MIKRO_DECORATED_BACKGROUND
    | typeof LAYER_WOHNLAGEN_MIKRO_KREIS
    | typeof LAYER_WOHNLAGEN_MIKRO_DECORATED_TOP
    | typeof LAYER_WOHNLAGEN_MAKRO
    | typeof LAYER_POIS_DECORATED
    | typeof LAYER_ALKIS
    | typeof LAYER_BASEMAP
    | typeof LAYER_COPERNICUS;

/** Loaded WMTS capabilities for a single layer: the raw XML capabilities document plus its matrix set. */
export interface WmtsCapabilitiesEntry {
    /** the WMTS GetCapabilities response as an XML document string */
    capabilities: string;
    /** the tile matrix set the capabilities were requested for */
    matrixset: string;
}

/** Loaded WMTS capabilities keyed by layer. Not every layer is necessarily present. */
export type WmtsCapabilities = Partial<Record<WmtsLayer, WmtsCapabilitiesEntry>>;

/** Fehler je Layer, dessen Capabilities nicht geladen werden konnten (#19906). */
export type WmtsLayerErrors = Partial<Record<WmtsLayer, ApiQueryError>>;

/** Ergebnis eines Ladevorgangs: die geladenen Layer und die Fehler der übrigen. */
export interface WmtsLoadResult {
    capabilities: WmtsCapabilities;
    layerErrors: Partial<Record<WmtsLayer, QueryError>>;
}

interface WmtsState {
    loading: boolean;
    /** nur die erfolgreich geladenen Layer; null, solange nichts geladen ist oder alle Layer fehlschlugen */
    capabilities: WmtsCapabilities | null;
    /** Fehler einzelner Layer; die übrigen Layer sind trotzdem in {@link capabilities} nutzbar */
    layerErrors: WmtsLayerErrors;
    /** nur gesetzt, wenn kein einziger Layer geladen werden konnte */
    queryError: ApiQueryError | null;
}

const initialState: WmtsState = {
    loading: false,
    capabilities: null,
    layerErrors: {},
    queryError: null,
};

const EMPTY_LAYER_ERRORS: WmtsLayerErrors = {};

export const wmtsSlice = createSlice({
    name: "wmts",
    initialState,
    reducers: {
        setLoading: (state) => {
            state.loading = true;
            state.capabilities = null;
            state.layerErrors = {};
            state.queryError = null;
        },
        /** Totalausfall: kein Layer ließ sich laden. Die Fehler je Layer bleiben zur Diagnose erhalten. */
        setQueryError: (
            state,
            action: PayloadAction<{queryError: QueryError; layerErrors?: WmtsLoadResult["layerErrors"]}>,
        ) => {
            state.loading = false;
            state.capabilities = null;
            state.layerErrors = buildLayerErrors(action.payload.layerErrors ?? {});
            state.queryError = buildApiQueryError(action.payload.queryError);
        },
        /** Mindestens ein Layer wurde geladen; fehlgeschlagene Layer stehen einzeln in layerErrors (#19906). */
        setCapabilities: (state, action: PayloadAction<WmtsLoadResult>) => {
            state.loading = false;
            state.capabilities = action.payload.capabilities;
            state.layerErrors = buildLayerErrors(action.payload.layerErrors);
            state.queryError = null;
        },
    },
    selectors: {
        selectCapabilities: (state) => state.capabilities,
        /**
         * Laden abgeschlossen — mit mindestens einem Layer oder als Totalausfall. Auch ein Totalausfall
         * gibt die App frei: eine Bewertung braucht keine Karte, die Karten zeigen dann ihren Hinweis (#19906).
         */
        selectCapabilitiesSettled: (state) => !state.loading && (!!state.capabilities || !!state.queryError),
        selectLoading: (state) => state.loading,
        /** query error data, or null if no error. Only set if no layer at all could be loaded. */
        selectError: (state) => state.queryError,
        /** Fehler der einzeln ausgefallenen Layer (leer, wenn alle geladen wurden) */
        selectLayerErrors: (state) => state.layerErrors ?? EMPTY_LAYER_ERRORS,
    },
});

function buildLayerErrors(layerErrors: WmtsLoadResult["layerErrors"]): WmtsLayerErrors {
    const result: WmtsLayerErrors = {};
    for (const [layer, error] of Object.entries(layerErrors) as [WmtsLayer, QueryError][]) {
        result[layer] = buildApiQueryError(error);
    }
    return result;
}

export const {setLoading, setQueryError, setCapabilities} = wmtsSlice.actions;

export const {selectLoading, selectError, selectCapabilities, selectCapabilitiesSettled, selectLayerErrors} =
    wmtsSlice.selectors;

export default wmtsSlice.reducer;
